/**
 * sl-67kc.3.3: LongRunningOpRow variant for the History panel.
 *
 * Rendered per active / recently-terminal long-running operation
 * attached to an execution log. Today the only kind is `stt` (Google
 * managed STT batch), future kinds (video-transcode, bulk-ai)
 * will reuse the same row with different metadata and actions.
 *
 * Actions matrix:
 *   - `pending`: Cancel + Details
 *   - `cancelling` / `resuming`: Details while the transition is in flight
 *   - `failed` (dispatch-side): Retry-from-Cache + Details (+ Download
 *     when `hasCachedResult`)
 *   - `failed` (provider-terminal): Details only (retry needs a fresh
 *     submission — the enclosing LogEntry's retry affordance triggers a
 *     new run; provider-terminal errors are non-retriable per
 *     SttJobsController.retryResume F6)
 *   - `completed` / `cancelled` / `timeout`: Details + Download when
 *     `hasCachedResult` (serves `SuspendedExecution.resumePayload`)
 *
 * Details inline expansion shows the full provider payload (jobId,
 * provider/model, submission + expiry timestamps, real duration,
 * failureOrigin, raw errorMessage). No round-trip — everything is on
 * the `LongRunningOpInfo` payload the backend enricher attaches.
 *
 * Cache-backed actions (Retry + Download-Transcript) read the canonical
 * result buffer stored on `SuspendedExecution.resumePayload`:
 *   - Retry: the existing `POST /api/stt/jobs/:jobId/retry-resume`
 *     endpoint re-enters `ExecutionResumeService`, which replays the
 *     resumePayload without re-submitting upstream (retry-from-cache
 *     semantics per `execution-resume-dlq.service.ts`).
 *   - Download-Transcript: `GET /api/stt/jobs/:jobId/transcript` serves
 *     the raw cached payload as a JSON file for manual inspection.
 *
 * `hasCachedResult` is derived server-side from a null-check on
 * `resumePayload` in `ExecutionLogLongRunningOpEnricher`, so the
 * frontend does not probe the endpoint speculatively.
 */
import { AnimatedRegion } from '@affine/core/components/animation/animated-region';
import { i18nTime, useI18n } from '@affine/i18n';
import { type FC, useState } from 'react';

import type { LongRunningOpInfo } from '../../../services/logging';
import * as styles from '../history-panel.css';
import { DataRow, ErrorBox } from './row-primitives';

const LRO_STATUS_TONE: Record<LongRunningOpInfo['status'], styles.HistoryTone> =
  {
    pending: 'warning',
    cancelling: 'warning',
    resuming: 'active',
    completed: 'success',
    failed: 'error',
    cancelled: 'neutral',
    timeout: 'error',
  };

const useLroStatusLabel = (status: LongRunningOpInfo['status']): string => {
  const t = useI18n();
  const keys: Record<LongRunningOpInfo['status'], string> = {
    pending: 'com.streamlain.streamlain.history.lro.status-pending',
    cancelling: 'com.streamlain.streamlain.history.lro.status-pending',
    resuming: 'com.streamlain.streamlain.history.lro.status-resuming',
    completed: 'com.streamlain.streamlain.history.lro.status-completed',
    failed: 'com.streamlain.streamlain.history.lro.status-failed',
    cancelled: 'com.streamlain.streamlain.history.lro.status-cancelled',
    timeout: 'com.streamlain.streamlain.history.lro.status-timeout',
  };
  return t[keys[status]]();
};

const formatIso = (iso: string): string => {
  const formatted = i18nTime(iso, { absolute: { accuracy: 'minute' } });
  return formatted || iso;
};

const formatSeconds = (s: number | null): string | null => {
  if (s === null || s === undefined) return null;
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rem = s % 60;
  return rem === 0 ? `${m}m` : `${m}m ${rem}s`;
};

type LroActionKind = 'cancel' | 'retry' | 'download';

export const LongRunningOpRow: FC<{
  op: LongRunningOpInfo;
  onCancel: () => Promise<void>;
  onRetry: () => Promise<void>;
  onDownload: () => Promise<void>;
}> = ({ op, onCancel, onRetry, onDownload }) => {
  const t = useI18n();
  const statusLabel = useLroStatusLabel(op.status);
  const [pending, setPending] = useState<LroActionKind | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  const canCancel = op.status === 'pending';
  const canRetry = op.status === 'failed' && op.failureOrigin !== 'provider';
  // sl-67kc.3.3 REWORK: Download-Transcript is available for any
  // terminal status whose `resumePayload` is populated. Provider-origin
  // failures typically have no cache, so `hasCachedResult` is the gate;
  // the button never renders speculatively.
  const canDownload = op.hasCachedResult;
  const realDuration = formatSeconds(op.realDurationSeconds);

  const handle = async (
    kind: LroActionKind,
    fn: () => Promise<void>
  ): Promise<void> => {
    setPending(kind);
    setActionError(null);
    try {
      await fn();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : String(err));
    } finally {
      setPending(null);
    }
  };

  const hasActions = canCancel || canRetry || canDownload;

  return (
    <div className={styles.lro}>
      <div className={styles.lroHeader}>
        <span className={styles.statusPill}>
          <span
            className={`${styles.dot} ${styles.dotTone[LRO_STATUS_TONE[op.status]]}`}
            aria-hidden="true"
          />
          {statusLabel}
        </span>
        <span className={styles.lroKind}>
          {t['com.streamlain.streamlain.history.lro.kind-stt']()}
        </span>
        {op.dynamicBatching && (
          <span
            className={styles.lroTag}
            title={t[
              'com.streamlain.streamlain.history.lro.dynamic-batching'
            ]()}
          >
            DYN
          </span>
        )}
      </div>
      <DataRow
        label={t['com.streamlain.streamlain.history.lro.provider']()}
        value={`${op.provider} / ${op.model}`}
      />
      <DataRow
        label={t['com.streamlain.streamlain.history.lro.submitted-at']()}
        value={formatIso(op.submittedAt)}
      />
      {op.status === 'pending' && (
        <DataRow
          label={t['com.streamlain.streamlain.history.lro.expires-at']()}
          value={formatIso(op.expiresAt)}
        />
      )}
      {realDuration !== null && (
        <DataRow
          label={t['com.streamlain.streamlain.history.lro.real-duration']()}
          value={realDuration}
        />
      )}
      {op.status === 'failed' && (
        <ErrorBox
          size="small"
          message={
            op.failureOrigin === 'provider'
              ? t['com.streamlain.streamlain.history.lro.failure-provider']() +
                (op.errorMessage ? ` (${op.errorMessage})` : '')
              : t['com.streamlain.streamlain.history.lro.failure-dispatch']() +
                (op.errorMessage ? ` (${op.errorMessage})` : '')
          }
        />
      )}
      {actionError && <ErrorBox size="small" message={actionError} />}
      <AnimatedRegion expanded={detailsOpen}>
        <div className={styles.lroDetails}>
          <DataRow
            label={t['com.streamlain.streamlain.history.lro.details.job-id']()}
            value={<code className={styles.mono}>{op.jobId}</code>}
          />
          <DataRow
            label={t[
              'com.streamlain.streamlain.history.lro.details.expires-at'
            ]()}
            value={formatIso(op.expiresAt)}
          />
          {op.failureOrigin && (
            <DataRow
              label={t[
                'com.streamlain.streamlain.history.lro.details.failure-origin'
              ]()}
              value={op.failureOrigin}
            />
          )}
          {op.errorMessage && (
            <DataRow
              label={t[
                'com.streamlain.streamlain.history.lro.details.error-message'
              ]()}
              value={op.errorMessage}
            />
          )}
          <DataRow
            label={t[
              'com.streamlain.streamlain.history.lro.details.dynamic-batching'
            ]()}
            value={op.dynamicBatching ? 'on' : 'off'}
          />
        </div>
      </AnimatedRegion>
      <div className={styles.lroActions}>
        {canCancel && (
          <button
            type="button"
            className={styles.lroAction}
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              void handle('cancel', onCancel);
            }}
            disabled={pending !== null}
          >
            {pending === 'cancel'
              ? t['com.streamlain.streamlain.history.lro.cancel-confirming']()
              : t['com.streamlain.streamlain.history.lro.cancel']()}
          </button>
        )}
        {canRetry && (
          <button
            type="button"
            className={styles.lroAction}
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              void handle('retry', onRetry);
            }}
            disabled={pending !== null}
          >
            {pending === 'retry'
              ? t['com.streamlain.streamlain.history.lro.retry-confirming']()
              : t['com.streamlain.streamlain.history.lro.retry']()}
          </button>
        )}
        {canDownload && (
          <button
            type="button"
            className={styles.lroAction}
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              void handle('download', onDownload);
            }}
            disabled={pending !== null}
          >
            {pending === 'download'
              ? t['com.streamlain.streamlain.history.lro.download-confirming']()
              : t['com.streamlain.streamlain.history.lro.download']()}
          </button>
        )}
        <button
          type="button"
          className={hasActions ? styles.smallButton : styles.lroAction}
          aria-expanded={detailsOpen}
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
            setDetailsOpen(o => !o);
          }}
        >
          {detailsOpen
            ? t['com.streamlain.streamlain.history.lro.details-hide']()
            : t['com.streamlain.streamlain.history.lro.details']()}
        </button>
      </div>
    </div>
  );
};
